import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
} from 'firebase/firestore';
import { Firestore } from 'firebase/firestore';
import type { Functions } from 'firebase/functions';
import { UnitInvitation, UserRole, Unit, User } from '../types';

export function useUnitInvitations(
  db: Firestore,
  functions: Functions | null | undefined,
  unitId: string | undefined,
  unit: Unit | null,
  currentUser: any,
  userProfile: User | null,
  members: Array<User & { role: UserRole; joinedAt: Date }>,
  activeTab: string,
  showSuccess: (message: string) => void,
  showError: (message: string) => void
) {
  const [invitations, setInvitations] = useState<UnitInvitation[]>([]);
  const [sendingInvite, setSendingInvite] = useState(false);

  useEffect(() => {
    const fetchInvitations = async () => {
      if (!unitId || activeTab !== 'invitations') return;

      try {
        const invitesQuery = query(
          collection(db, 'invitations'),
          where('unitId', '==', unitId),
          where('status', '==', 'pending')
        );
        const invitesSnapshot = await getDocs(invitesQuery);
        const invites: UnitInvitation[] = invitesSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            unitId: data.unitId,
            unitName: data.unitName,
            email: data.email,
            role: data.role,
            invitedBy: data.invitedBy,
            invitedByName: data.invitedByName,
            status: data.status,
            createdAt: data.createdAt?.toDate() || new Date(),
            expiresAt: data.expiresAt?.toDate() || new Date(),
            acceptedAt: data.acceptedAt?.toDate(),
            token: data.token,
            userExists: data.userExists,
          };
        });
        setInvitations(invites);
      } catch (error) {
        console.error('Error fetching invitations:', error);
      }
    };

    fetchInvitations();
  }, [unitId, activeTab, db]);

  const handleSendInvitation = async (inviteEmail: string, inviteRole: UserRole) => {
    if (!unit || !currentUser || !userProfile) return false;

    if (!inviteEmail.trim()) {
      showError('Please enter an email address');
      return false;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteEmail)) {
      showError('Please enter a valid email address');
      return false;
    }

    // Check if user is already a member
    const existingMember = members.find((m) => m.email === inviteEmail);
    if (existingMember) {
      showError('This user is already a member of the unit');
      return false;
    }

    // Check if invitation already exists
    const existingInvite = invitations.find((i) => i.email === inviteEmail);
    if (existingInvite) {
      showError('An invitation has already been sent to this email');
      return false;
    }

    if (!functions) {
      showError('Invitation service is not available. Please refresh and try again.');
      return false;
    }

    setSendingInvite(true);
    try {
      const { httpsCallable } = await import('firebase/functions');
      const createUnitInvitation = httpsCallable<
        { unitId: string; email: string; role: UserRole },
        { invitationId: string; userExists: boolean }
      >(functions, 'createUnitInvitation');

      const result = await createUnitInvitation({
        unitId: unit.id,
        email: inviteEmail.toLowerCase(),
        role: inviteRole,
      });

      const userExists = result.data.userExists;

      if (userExists) {
        showSuccess(
          `Invitation sent to ${inviteEmail}. They can accept it from their dashboard or via the invitation link.`
        );
      } else {
        showSuccess(
          `Invitation sent to ${inviteEmail}. They will need to create an account first before they can join your unit.`
        );
      }

      // Refresh invitations
      const invitesQuery = query(
        collection(db, 'invitations'),
        where('unitId', '==', unitId),
        where('status', '==', 'pending')
      );
      const invitesSnapshot = await getDocs(invitesQuery);
      const invites: UnitInvitation[] = invitesSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          unitId: data.unitId,
          unitName: data.unitName,
          email: data.email,
          role: data.role,
          invitedBy: data.invitedBy,
          invitedByName: data.invitedByName,
          status: data.status,
          createdAt: data.createdAt?.toDate() || new Date(),
          expiresAt: data.expiresAt?.toDate() || new Date(),
          acceptedAt: data.acceptedAt?.toDate(),
          token: data.token,
        };
      });
      setInvitations(invites);

      return true;
    } catch (error: any) {
      console.error('Error sending invitation:', error);
      const code = error?.code;
      const message = error?.message || '';
      if (code === 'functions/already-exists' || code === 'already-exists') {
        showError(message || 'An invitation has already been sent to this email');
      } else if (code === 'functions/permission-denied' || code === 'permission-denied') {
        showError('You do not have permission to invite members to this unit');
      } else {
        showError('Failed to send invitation');
      }
      return false;
    } finally {
      setSendingInvite(false);
    }
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      await deleteDoc(doc(db, 'invitations', invitationId));
      setInvitations(invitations.filter((i) => i.id !== invitationId));
      showSuccess('Invitation revoked successfully');
      return true;
    } catch (error) {
      console.error('Error revoking invitation:', error);
      showError('Failed to revoke invitation');
      return false;
    }
  };

  return {
    invitations,
    sendingInvite,
    handleSendInvitation,
    handleRevokeInvitation,
  };
}

